// ABOUTME: Admin dashboard overview page showing workspace summary and quick stats.
// ABOUTME: Entry point for admin navigation to workspaces, usage, and config.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CheckCircle,
  Clock,
  Star,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';
import { workspaceApi } from '@/services/api/adminClient';
import type { WorkspaceListItem } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface DashboardStats {
  totalWorkspaces: number;
  activeWorkspaces: number;
  trialWorkspaces: number;
  enterpriseWorkspaces: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalWorkspaces: 0,
    activeWorkspaces: 0,
    trialWorkspaces: 0,
    enterpriseWorkspaces: 0,
  });
  const [recentWorkspaces, setRecentWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<WorkspaceListItem[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      try {
        setLoading(true);
        const response = await workspaceApi.list({ page: 1, page_size: 10 });

        // Calculate stats from response
        const workspaces = response.workspaces;
        setStats({
          totalWorkspaces: response.total,
          activeWorkspaces: workspaces.filter(w => w.subscription_status === 'active').length,
          trialWorkspaces: workspaces.filter(w => w.subscription_status === 'trial').length,
          enterpriseWorkspaces: workspaces.filter(
            w => w.subscription_tier === 'enterprise' || w.subscription_tier === 'enterprise_plus'
          ).length,
        });
        setRecentWorkspaces(workspaces.slice(0, 5));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await workspaceApi.list({
          page: 1,
          page_size: 10,
          search: value.trim(),
        });
        setSearchResults(response.workspaces);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchLoading(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-surface-800">Admin Dashboard</h1>
          <p className="text-sm text-surface-600 mt-1">
            Overview of your workspace management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/workspaces">
            <Button>View All Workspaces</Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Workspaces"
          value={stats.totalWorkspaces}
          icon={<Building2 className="w-6 h-6" />}
          color="indigo"
        />
        <StatCard
          title="Active Workspaces"
          value={stats.activeWorkspaces}
          icon={<CheckCircle className="w-6 h-6" />}
          color="teal"
        />
        <StatCard
          title="Trial Workspaces"
          value={stats.trialWorkspaces}
          icon={<Clock className="w-6 h-6" />}
          color="warning"
        />
        <StatCard
          title="Enterprise"
          value={stats.enterpriseWorkspaces}
          icon={<Star className="w-6 h-6" />}
          color="coral"
        />
      </div>

      {/* Workspace Search + Recent Workspaces */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-surface-800">
            {searchResults !== null ? 'Search Results' : 'Recent Workspaces'}
          </h2>
          <Link
            to="/admin/workspaces"
            className="text-sm text-primary-600 hover:underline"
          >
            View all
          </Link>
        </div>

        {/* Search bar */}
        <div className="relative mb-4">
          <Input
            placeholder="Search workspaces by name or slug..."
            value={searchQuery}
            leftIcon={<Search className="w-4 h-4" />}
            rightIcon={
              searchQuery ? (
                <button
                  onClick={clearSearch}
                  className="pointer-events-auto cursor-pointer text-surface-400 hover:text-surface-600"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : undefined
            }
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {/* Results */}
        <div className="space-y-4">
          {searchLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : (searchResults !== null ? searchResults : recentWorkspaces).length === 0 ? (
            <Card variant="glass" className="p-8 text-center">
              <Building2 className="w-12 h-12 text-surface-500 mx-auto mb-4" />
              <p className="text-surface-600">
                {searchResults !== null ? 'No workspaces match your search' : 'No workspaces found'}
              </p>
            </Card>
          ) : (
            (searchResults !== null ? searchResults : recentWorkspaces).map((workspace) => (
              <Link key={workspace.id} to={`/admin/workspaces/${workspace.id}`} className="block">
                <Card variant="surface" interactive className="hover:border-primary-600/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-surface-800">
                          {workspace.name}
                        </h3>
                        <p className="text-xs text-surface-500">{workspace.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right mr-4">
                        <p className="text-xs text-surface-500">Data Sources</p>
                        <p className="text-sm font-medium text-surface-800">
                          {workspace.data_sources_count}
                        </p>
                      </div>
                      <TierBadge tier={workspace.subscription_tier} />
                      <StatusBadge status={workspace.subscription_status} />
                      <ChevronRight className="w-4 h-4 text-surface-500" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>

    </div>
  );
}

// Helper Components

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'indigo' | 'teal' | 'warning' | 'coral';
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  const colorClasses = {
    indigo: 'bg-primary-50 text-primary-600',
    teal: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    coral: 'bg-error-light text-error',
  };

  return (
    <Card variant="surface">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-sm ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-surface-500">{title}</p>
          <p className="text-2xl font-semibold text-surface-800">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    freemium: 'bg-surface-100 text-surface-500',
    basic: 'bg-primary-50 text-primary-600',
    standard: 'bg-success-light text-success',
    enterprise: 'bg-warning-light text-warning',
    enterprise_plus: 'bg-error-light text-error',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[tier] || colors.freemium}`}>
      {tier.replace('_', ' ')}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-success-light text-success',
    trial: 'bg-warning-light text-warning',
    suspended: 'bg-error-light text-error',
    cancelled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.active}`}>
      {status}
    </span>
  );
}

export default AdminDashboard;
